import React, { useEffect, useState } from "react";
import { Upload, message, Image } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import { useDispatch, useSelector } from "react-redux";
import {
  addFile,
  removeStagedFile,
  markFileForDeletion,
  removeExistingFile,
} from "./AttachmentsSlice";
import { useIntl } from "react-intl";

const SingleImageUploader = (props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const attachments = useSelector((state) => state.Attachments.attachments);

  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  useEffect(() => {
    const documents =
      attachments?.[props?.identifier]?.[props?.currentId]?.documents?.filter(
        (doc) => !doc.isThumbnail
      ) || [];

    const mapped = documents.map((doc, i) => ({
      uid: doc.guidFileName || `existing-${i}`,
      name: doc.originalFileName || `image-${i + 1}.jpg`,
      status: "done",
      url: `data:image/jpeg;base64,${doc.base64Content}`,
      isExisting: true,
      id: doc.id,
    }));

    setFileList(mapped);
    if (mapped.length > 0) {
      props.onChange?.(mapped);
    }
  }, [attachments, props?.identifier, props?.currentId]);

  const onChange = async ({ file, fileList: newList }) => {
    if (file.status === "removed") {
      if (file.isExisting) {
        dispatch(
          markFileForDeletion({
            identifier: props?.identifier,
            id: props?.currentId,
            fileId: file.id,
          })
        );
        dispatch(
          removeExistingFile({
            identifier: props?.identifier,
            id: props?.currentId,
            fileId: file.id,
          })
        );
      } else {
        dispatch(
          removeStagedFile({
            identifier: props?.identifier,
            id: props?.currentId,
            fileName: file.name,
          })
        );
      }
      const updatedList = newList.filter((f) => f.uid !== file.uid);
      setFileList(updatedList);
      props.onChange?.(updatedList);
      return;
    }

    if (file.originFileObj) {
      const base64 = await getBase64(file.originFileObj);
      dispatch(
        addFile({
          fileName: file.name,
          base64: base64.split(",")[1],
          identifier: props?.identifier,
          id: props?.currentId,
        })
      );
      const updatedList = [
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: base64,
        },
      ];
      setFileList(updatedList);
      props.onChange?.(updatedList);
    }
  };

  const onPreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  return (
    <>
      <Upload
        listType="picture-card"
        fileList={fileList}
        onChange={onChange}
        onPreview={onPreview}
        customRequest={() => {}}
        accept="image/*"
        showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
        beforeUpload={(file) => {
          const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name);
          const isLt25MB = file.size / 1024 / 1024 < 25;
          if (!isImage) {
            message.warning(
              intl.formatMessage({
                id: "Attachments.Validation.OnlyImage",
              })
            );
            return false;
          }
          if (!isLt25MB) {
            message.warning(
              intl.formatMessage({ id: "Attachments.Validation.MaxSize" })
            );
            return false;
          }
          return true;
        }}
      >
        {fileList.length < 1 && (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
          </div>
        )}
      </Upload>

      {previewImage && (
        <Image
          wrapperStyle={{ display: "none" }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(""),
          }}
          src={previewImage}
        />
      )}
    </>
  );
};

export default SingleImageUploader;
